import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { formatISOTimeForDisplay } from '@plato/shared-util/time-utils';
import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Dialog, DialogTitle } from '@plato/ui/containers/dialog';
import { formatISO, intlFormat, isSameDay, parseISO } from 'date-fns';
import type { LucideProps } from 'lucide-react';
import {
    AlignLeftIcon,
    CalendarIcon,
    CheckIcon,
    CircleUserIcon,
    ClockIcon,
    InfoIcon,
    MapPinIcon,
    PencilIcon,
    RefreshCwIcon,
    Trash2Icon,
    TypeIcon,
    XIcon,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { buildGoogleMapsSearchUrl } from '../../../lib/google-maps-url.ts';
import { api } from '../../../utils/api.ts';
import { isEventOccurrenceLocked } from '../../../utils/event-lock-utils.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { EventUpdatedSource } from '../../analytics/types/analytics-events.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { EditNoteDialog } from '../../notes/components/edit-note-dialog.tsx';
import { LogInteractionDialog } from '../../notes/components/log-interaction-dialog.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { DeleteEventModal } from './delete-event-modal.tsx';
import { EventEditDialog } from './event-edit-dialog.tsx';
import { EventLogButton } from './event-log-button.tsx';
import { EventLoggedNoteSummary } from './event-logged-note-summary.tsx';

type EventDetailsDialogProps = ModalInnerProps & {
    eventId: string;
    date: string;
    /** Where the user opened this event from; forwarded to the edit dialog for event_updated. */
    analyticsSource: EventUpdatedSource;
};

export type InfoTagProps = {
    icon: ComponentType<LucideProps>;
} & React.HTMLAttributes<HTMLDivElement>;

export function InfoTag({ icon: Icon, children, ...props }: InfoTagProps): ReactNode {
    if (!children) {
        return null;
    }

    return (
        <div className="flex items-center overflow-hidden gap-2" {...props}>
            <Icon className="h-4 w-4 shrink-0 text-neutral-500" />
            {children}
        </div>
    );
}

function EventDetailsDialogInternal({ eventId, date, analyticsSource }: EventDetailsDialogProps): ReactNode {
    const { visible, remove } = useDialog();
    const { t } = useTranslation('events');
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();
    const navigate = useNavigate();
    const utils = api.useUtils();

    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        { eventId },
        { enabled: !!eventId },
    );

    const isOwner = eventDetails?.owner_id === userId;

    const occurrenceDateObj = parseISO(date);
    const linkedNotes =
        eventDetails?.notes.filter((note) => isSameDay(note.occurredAt, occurrenceDateObj)) ?? [];

    const handleEdit = (): void => {
        if (!eventDetails) {
            return;
        }

        remove();

        void DialogStateManager.show(EventEditDialog, {
            eventId,
            eventDetails,
            occurrenceDate: date,
            analyticsSource,
        });
    };

    const refreshAndClose = (): void => {
        if (!eventDetails) {
            return;
        }

        void Promise.all([
            utils.platoUser.query.events.getEventById.invalidate({
                eventId: eventDetails.event_id,
            }),
            utils.platoUser.query.events.getList.invalidate(),
            (async () => {
                if (eventDetails.customer?.customerId) {
                    await utils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                        customerId: eventDetails.customer.customerId,
                    });
                }
            })(),
        ]);

        remove();
    };

    const handleOpenNote = (noteId: string): void => {
        void DialogStateManager.show(EditNoteDialog, {
            onDelete: refreshAndClose,
            onSuccess: refreshAndClose,
            noteId,
        });
    };

    const handleLog = (): void => {
        if (!eventDetails) {
            return;
        }

        const existingNote = linkedNotes[0];

        if (existingNote) {
            handleOpenNote(existingNote.noteId);
        } else {
            void DialogStateManager.show(LogInteractionDialog, {
                event: eventDetails,
                customer: eventDetails.customer,
                analyticsSource: 'event_drawer',
                onSuccess: refreshAndClose,
            });
        }
    };

    const handleDelete = (): void => {
        if (!eventDetails) {
            return;
        }

        void DialogStateManager.show(DeleteEventModal, {
            date,
            event: eventDetails,
            onDeleted: () => {
                remove();
                sendEvent('event_deleted', getEventMetadata(eventDetails));
            },
        });
    };

    const dateFormatted = date
        ? intlFormat(
              parseISO(date),
              { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' },
              { locale },
          )
        : '';

    const timeFormatted =
        eventDetails?.start_time && eventDetails.end_time
            ? t('card_time_format', {
                  startTime: formatISOTimeForDisplay(eventDetails.start_time),
                  endTime: formatISOTimeForDisplay(eventDetails.end_time),
              })
            : eventDetails?.start_time
              ? formatISOTimeForDisplay(eventDetails.start_time)
              : '';

    const recurrenceWeeksCount = eventDetails?.recurrenceRule?.weeks ?? 0;

    // STEER-121: an occurrence with a note on the same or a later day is locked — editing,
    // moving, or deleting it would detach or mis-date the note. The API enforces the same rule.
    const isLocked =
        !!eventDetails &&
        isEventOccurrenceLocked({
            isRecurring: !!eventDetails.recurrenceRule,
            latestNoteDate: eventDetails.latestNoteDate,
            occurrenceDate: date,
        });
    const hasLoggedNote = linkedNotes.length > 0;
    const isFutureDate = date > formatISO(new Date(), { representation: 'date' });
    const showLogButton = isOwner && !isFutureDate;
    const showEditButton = isOwner && !isLocked;
    const showDeleteButton = isOwner && !isLocked;

    if (isLoading || !eventDetails) {
        return (
            <Dialog open={visible} onClose={remove} disablePointerDismissal>
                <DialogForm className="sm:max-w-[450px]">
                    <DialogTitle className="sr-only">
                        {t('events__card_default_title', { ns: 'events' })}
                    </DialogTitle>
                    <DialogForm.Content>
                        <LoadingBlockFullPage className="mt-16" />
                    </DialogForm.Content>
                </DialogForm>
            </Dialog>
        );
    }

    const googleAddressLink = buildGoogleMapsSearchUrl({ addressLine1: eventDetails.address });

    return (
        <Dialog open={visible} onClose={remove} disablePointerDismissal>
            <DialogForm className="sm:max-w-[450px]">
                <DialogTitle className="sr-only">
                    {eventDetails.title ?? t('events__card_default_title', { ns: 'events' })}
                </DialogTitle>
                <DialogForm.Content className="gap-4 p-5">
                    <div className="flex items-center justify-end gap-3">
                        <div className="flex items-center gap-1">
                            {showEditButton && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    icon={PencilIcon}
                                    aria-label={t('events__edit', { ns: 'events' })}
                                    onClick={handleEdit}
                                />
                            )}
                            {showDeleteButton && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    icon={Trash2Icon}
                                    aria-label={t('events__delete', { ns: 'events' })}
                                    onClick={handleDelete}
                                />
                            )}
                        </div>
                        {showLogButton && (
                            <EventLogButton
                                isLogged={hasLoggedNote}
                                disabled={!eventDetails.customerId}
                                onClick={handleLog}
                            />
                        )}
                        {/* STEER-252: non-owners get a read-only logged indicator */}
                        {!isOwner && hasLoggedNote && (
                            <div className="flex items-center gap-1.5 text-green-600">
                                <CheckIcon className="h-4 w-4" />
                                <Text color="inherit" weight="medium">
                                    {t('card_log_button_logged')}
                                </Text>
                            </div>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            icon={XIcon}
                            aria-label={t('events__close', { ns: 'events' })}
                            onClick={remove}
                        />
                    </div>
                    <InfoTag icon={TypeIcon}>
                        <Text color="neutral-900" size="lg" weight="medium">
                            {eventDetails.title ?? t('events__card_default_title')}
                        </Text>
                    </InfoTag>
                    {eventDetails.customer && (
                        <InfoTag icon={CustomerIcon}>
                            <LinkText
                                href={`/customers/${encodeURIComponent(eventDetails.customer.customerId)}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    remove();
                                    void navigate(
                                        `/customers/${encodeURIComponent(eventDetails.customerId ?? '')}`,
                                    );
                                }}
                            >
                                {eventDetails.customer.customerId} {eventDetails.customer.customerName}
                            </LinkText>
                        </InfoTag>
                    )}
                    {googleAddressLink && (
                        <InfoTag icon={MapPinIcon}>
                            <LinkText href={googleAddressLink} target="_blank" rel="noopener noreferrer">
                                {eventDetails.address}
                            </LinkText>
                        </InfoTag>
                    )}
                    {!!dateFormatted && (
                        <InfoTag icon={CalendarIcon}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <Text color="neutral-900">{dateFormatted}</Text>
                                {!!recurrenceWeeksCount && (
                                    <span className="flex items-center gap-1.5">
                                        <RefreshCwIcon className="h-3.5! w-3.5!" />
                                        <Text color="neutral-900">
                                            {t('card_recurrence_weeks', { count: recurrenceWeeksCount })}
                                        </Text>
                                    </span>
                                )}
                            </div>
                        </InfoTag>
                    )}
                    {!!timeFormatted && (
                        <InfoTag icon={ClockIcon}>
                            <Text color="neutral-900">{timeFormatted}</Text>
                        </InfoTag>
                    )}
                    {eventDetails.agendaRich && (
                        <InfoTag icon={AlignLeftIcon}>
                            <RichTextViewer content={eventDetails.agendaRich} />
                        </InfoTag>
                    )}
                    {eventDetails.owner_name && (
                        <InfoTag icon={CircleUserIcon}>
                            <Text color="neutral-500">
                                {t('events__form_created_by', { ownerName: eventDetails.owner_name })}
                            </Text>
                        </InfoTag>
                    )}
                    {linkedNotes.map((note) => (
                        <EventLoggedNoteSummary
                            key={note.noteId}
                            note={note}
                            onOpen={() => {
                                handleOpenNote(note.noteId);
                            }}
                        />
                    ))}
                    {showLogButton && !eventDetails.customer && (
                        <InfoTag icon={InfoIcon}>
                            <Text color="neutral-500">{t('card_protocol__no_customer_hint')}</Text>
                        </InfoTag>
                    )}
                </DialogForm.Content>
            </DialogForm>
        </Dialog>
    );
}

export const EventDetailsDialog =
    DialogStateManager.create<EventDetailsDialogProps>(EventDetailsDialogInternal);
